import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { PODCAST_PLAYER_FIRMWARE } from '../upload/fetchFirmware'
import { type UploadFile } from '../upload/uploadAudio'
import { useToyMaker } from '../upload/useToyMaker'
import { disposeWorker } from '../audio/audioWorkerClient'
import PodcastSearchBar from '../components/PodcastPlayer/PodcastSearchBar'
import PodcastSearchResults from '../components/PodcastPlayer/PodcastSearchResults'
import PodcastDetailPanel from '../components/PodcastPlayer/PodcastDetailPanel'
import SelectedEpisodesTray from '../components/PodcastPlayer/SelectedEpisodesTray'
import {
  getBestPodcasts,
  getPodcast,
  searchPodcasts,
} from '../components/PodcastPlayer/podcastApi'
import {
  runEpisodePipeline,
  type EpisodePipelineEvent,
} from '../components/PodcastPlayer/buildEpisodePipeline'
import {
  buildProgressReducer,
  initialBuildProgress,
  type EpisodeBuildStatus,
} from '../components/PodcastPlayer/buildProgress'
import type { Episode } from '../components/PodcastPlayer/types'
import {
  EPISODE_CAP,
  initialState,
  reducer,
} from '../components/PodcastPlayer/reducer'
import './MakeMusicPlayer.css'
import './MakePodcastPlayer.css'

const SEARCH_DEBOUNCE_MS = 350
const MIN_QUERY_LEN = 2
const INITIAL_GENRE_ID = 132

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function statusForEvent(e: EpisodePipelineEvent): EpisodeBuildStatus {
  switch (e.type) {
    case 'downloading':
      return { phase: 'downloading' }
    case 'queued':
      return { phase: 'queued' }
    case 'encoding':
      return { phase: 'encoding', percent: e.percent }
    case 'done':
      return { phase: 'done' }
    case 'failed':
      return { phase: 'failed' }
  }
}

export default function MakePodcastPlayer() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [buildProgress, dispatchBuild] = useReducer(
    buildProgressReducer,
    initialBuildProgress,
  )

  const searchSeqRef = useRef(0)
  const lastSearchedQueryRef = useRef<string>('')

  useEffect(() => () => disposeWorker(), [])

  const runSearch = useCallback(async (q: string, offset: number, append: boolean) => {
    const seq = ++searchSeqRef.current
    dispatch({ type: 'searchStart' })
    try {
      const { results, nextOffset } = await searchPodcasts(q, offset)
      if (searchSeqRef.current !== seq) return
      dispatch({ type: 'searchSuccess', results, nextOffset, append })
    } catch (err) {
      if (searchSeqRef.current !== seq) return
      const message = err instanceof Error ? err.message : 'Search failed'
      dispatch({ type: 'searchError', message })
    }
  }, [])

  useEffect(() => {
    const trimmed = state.query.trim()
    if (trimmed.length < MIN_QUERY_LEN) {
      searchSeqRef.current++
      lastSearchedQueryRef.current = ''
      return
    }
    if (trimmed === lastSearchedQueryRef.current) return
    const handle = window.setTimeout(() => {
      lastSearchedQueryRef.current = trimmed
      runSearch(trimmed, 0, false)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [state.query, runSearch])

  // Seed the results pane with a "best podcasts" list on first mount.
  // Must run AFTER the search-debounce effect above so its
  // searchSeqRef bump (for the empty initial query) happens first.
  useEffect(() => {
    const seq = ++searchSeqRef.current
    dispatch({ type: 'searchStart' })
    getBestPodcasts(INITIAL_GENRE_ID)
      .then((results) => {
        if (searchSeqRef.current !== seq) return
        dispatch({
          type: 'searchSuccess',
          results,
          nextOffset: null,
          append: false,
        })
      })
      .catch((err) => {
        if (searchSeqRef.current !== seq) return
        const message =
          err instanceof Error ? err.message : 'Failed to load podcasts'
        dispatch({ type: 'searchError', message })
      })
  }, [])

  const handleLoadMoreSearch = useCallback(() => {
    if (state.search.nextOffset === null) return
    const trimmed = state.query.trim()
    if (trimmed.length < MIN_QUERY_LEN) return
    runSearch(trimmed, state.search.nextOffset, true)
  }, [runSearch, state.query, state.search.nextOffset])

  const handleRetrySearch = useCallback(() => {
    const trimmed = state.query.trim()
    if (trimmed.length < MIN_QUERY_LEN) return
    lastSearchedQueryRef.current = trimmed
    runSearch(trimmed, 0, false)
  }, [runSearch, state.query])

  const loadPodcastDetail = useCallback(
    async (id: string, nextPubDateMs: number | undefined, append: boolean) => {
      dispatch({ type: 'detailStart', id })
      try {
        const { podcast, episodes, nextPubDateMs: nextRaw } = await getPodcast(
          id,
          nextPubDateMs,
        )
        dispatch({
          type: 'detailSuccess',
          id,
          podcast,
          episodes,
          nextPubDateMs: nextRaw,
          append,
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load podcast'
        dispatch({ type: 'detailError', id, message })
      }
    },
    [],
  )

  const handleSelectPodcast = useCallback(
    (id: string) => {
      dispatch({ type: 'openPodcast', id })
      const existing = state.detailsByPodcastId[id]
      if (!existing || (!existing.podcast && !existing.loading)) {
        loadPodcastDetail(id, undefined, false)
      }
    },
    [loadPodcastDetail, state.detailsByPodcastId],
  )

  const handleBackToResults = useCallback(() => {
    dispatch({ type: 'closePodcast' })
  }, [])

  const handleLoadMoreEpisodes = useCallback(() => {
    const id = state.activePodcastId
    if (!id) return
    const detail = state.detailsByPodcastId[id]
    if (!detail || detail.nextPubDateMs === null) return
    loadPodcastDetail(id, detail.nextPubDateMs, true)
  }, [loadPodcastDetail, state.activePodcastId, state.detailsByPodcastId])

  const handleAdd = useCallback((episode: Episode) => {
    dispatch({ type: 'addEpisode', episode })
  }, [])

  const handleAddBatch = useCallback(() => {
    const id = state.activePodcastId
    if (!id) return
    const detail = state.detailsByPodcastId[id]
    if (!detail) return
    const selectedSet = new Set(state.selected.map((e) => e.id))
    const remainingSlots = EPISODE_CAP - state.selected.length
    if (remainingSlots <= 0) return
    // Episodes come back newest-first; iterate from the end to add oldest first.
    let added = 0
    for (let i = detail.episodes.length - 1; i >= 0 && added < remainingSlots; i--) {
      const ep = detail.episodes[i]
      if (selectedSet.has(ep.id)) continue
      dispatch({ type: 'addEpisode', episode: ep })
      added++
    }
  }, [state.activePodcastId, state.detailsByPodcastId, state.selected])

  const handleRemove = useCallback((id: string) => {
    dispatch({ type: 'removeEpisode', id })
  }, [])

  const handleMoveUp = useCallback((index: number) => {
    dispatch({ type: 'moveEpisode', index, direction: 'up' })
  }, [])

  const handleMoveDown = useCallback((index: number) => {
    dispatch({ type: 'moveEpisode', index, direction: 'down' })
  }, [])

  const handleClearSelection = useCallback(() => {
    dispatch({ type: 'clearSelection' })
  }, [])

  const hasSelected = state.selected.length > 0
  const canAdd = state.selected.length < EPISODE_CAP

  const [footerStatusEl, setFooterStatusEl] = useState<HTMLElement | null>(null)

  // Pre-fill the "Tell us about your toy" dialog from the first selected
  // episode's podcast. Falls back to undefined (empty modal) if the
  // podcast detail isn't cached yet.
  const firstSelected = state.selected[0]
  const detailsDefaults = firstSelected
    ? (() => {
        const podcast = state.detailsByPodcastId[firstSelected.podcastId]?.podcast
        if (!podcast) return undefined
        const thumb = podcast.image || podcast.thumbnail
        return {
          title: podcast.title.slice(0, 120),
          description: podcast.description.slice(0, 500),
          thumbnailUrl: thumb || undefined,
        }
      })()
    : undefined

  const toyMaker = useToyMaker({
    makerApp: 'podcast-player',
    hasPayload: hasSelected,
    firmwareTarget: PODCAST_PLAYER_FIRMWARE,
    appNameFallback: 'Oroblanco Podcast Player',
    detailsDefaults,
    statusContainer: footerStatusEl,
    buildPayload: async () => {
      // Frozen snapshot: the pipeline and its events index into this
      // array even if the selection changes mid-build.
      const selected = state.selected
      const total = selected.length

      dispatchBuild({ type: 'start', episodeIds: selected.map((e) => e.id) })
      toyMaker.setUpload({
        status: 'preparing',
        folder: '',
        step: `Preparing episodes — 0 of ${total} done`,
      })

      let settled = 0
      const onEvent = (e: EpisodePipelineEvent) => {
        dispatchBuild({
          type: 'set',
          episodeId: selected[e.index].id,
          status: statusForEvent(e),
        })
        if (e.type === 'done' || e.type === 'failed') {
          settled++
          toyMaker.setUpload({
            status: 'preparing',
            folder: '',
            step: `Preparing episodes — ${settled} of ${total} done`,
          })
        }
      }

      try {
        const { blobs, failedTitles } = await runEpisodePipeline(selected, onEvent)

        const payloadFiles: UploadFile[] = []
        const includedEpisodes: Episode[] = []
        for (let i = 0; i < total; i++) {
          const blob = blobs[i]
          if (!blob) continue
          payloadFiles.push({ name: `${pad2(payloadFiles.length + 1)}.mp3`, blob })
          includedEpisodes.push(selected[i])
        }

        if (failedTitles.length > 0) {
          const count = failedTitles.length
          window.alert(
            `Couldn't download audio for ${count} episode${count === 1 ? '' : 's'} (likely a CORS redirect from the publisher's CDN). ${count === 1 ? 'It' : 'They'} won't be included:\n\n` +
              failedTitles.map((t) => `• ${t}`).join('\n'),
          )
        }
        return {
          payloadFiles,
          extraMetadata: {
            episodes: includedEpisodes.map((ep) => ({
              id: ep.id,
              title: ep.title,
              audio: ep.audio,
              podcastId: ep.podcastId,
              podcastTitle: ep.podcastTitle,
            })),
          },
        }
      } finally {
        dispatchBuild({ type: 'reset' })
      }
    },
  })

  const activeDetail = state.activePodcastId
    ? state.detailsByPodcastId[state.activePodcastId]
    : undefined

  const selectedIds = new Set(state.selected.map((e) => e.id))

  const showFooterStatus =
    toyMaker.isWorking || toyMaker.upload.status === 'error'

  return (
    <section className="podcast-player">
      <Link to="/" className="detail-back">&larr; Back to Toys</Link>
      <h1 className="page-title">Podcast Player</h1>
      <p className="page-lede">
        Search for podcasts, pick up to {EPISODE_CAP} episodes, and we&rsquo;ll pack them
        onto a toy you can listen to.
      </p>

      <div className="podcast-player__search-block">
        <PodcastSearchBar
          value={state.query}
          onChange={(q) => dispatch({ type: 'setQuery', query: q })}
        />
        <a
          className="podcast-player__attribution"
          href="https://www.listennotes.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="https://brand-assets-cdn.listennotes.com/brand-assets-listennotes-com/production/media/image-4d0759f7f5c9110121222dbf031886dd.png"
            alt="Powered by Listen Notes"
            className="podcast-player__attribution-img"
          />
        </a>
      </div>

      <div className="podcast-player__grid">
        <div className="podcast-player__column podcast-player__column--results">
          {activeDetail ? (
            <PodcastDetailPanel
              state={{
                loading: activeDetail.loading,
                error: activeDetail.error,
                podcast: activeDetail.podcast,
                episodes: activeDetail.episodes,
                hasMore: activeDetail.nextPubDateMs !== null,
              }}
              selectedIds={selectedIds}
              canAdd={canAdd}
              onAdd={handleAdd}
              onAddBatch={handleAddBatch}
              onRemove={handleRemove}
              onLoadMore={handleLoadMoreEpisodes}
              onBack={handleBackToResults}
            />
          ) : (
            <PodcastSearchResults
              results={state.search.results}
              activePodcastId={state.activePodcastId}
              loading={state.search.loading}
              error={state.search.error}
              hasMore={state.search.nextOffset !== null}
              query={state.query}
              onSelect={handleSelectPodcast}
              onLoadMore={handleLoadMoreSearch}
              onRetry={handleRetrySearch}
            />
          )}
        </div>

        <div
          id="selected-episodes"
          className="podcast-player__column podcast-player__column--tray"
        >
          <SelectedEpisodesTray
            episodes={state.selected}
            cap={EPISODE_CAP}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            onRemove={handleRemove}
            onClear={handleClearSelection}
            buildStatuses={buildProgress.building ? buildProgress.byId : undefined}
          />
        </div>
      </div>

      <div className="podcast-player__sticky-footer">
        {showFooterStatus ? (
          <div
            ref={setFooterStatusEl}
            className="podcast-player__footer-status"
            aria-live="polite"
          />
        ) : (
          <a
            href="#selected-episodes"
            className="podcast-player__count-link"
            onClick={(e) => {
              e.preventDefault()
              document
                .getElementById('selected-episodes')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
          >
            {state.selected.length} of {EPISODE_CAP} selected
          </a>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!toyMaker.canSubmit}
          onClick={toyMaker.onDoneClick}
        >
          {toyMaker.isWorking ? 'Working…' : 'Done, make my toy'}
        </button>
      </div>

      {toyMaker.modals}
    </section>
  )
}
